import { faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { isNumber, setSoilColor, roundNumber } from '../../../../utils';
import { SectionTitle } from '../../../../components';
import SoilLayer from './SoilLayer';
import styles from './index.module.css';
import stringify from 'fast-json-stable-stringify';
import useDeepCompareEffect from 'use-deep-compare-effect';

const SoilProfile = ({
    height = [],
    density = [],
    plasticity = [],
    edometricModule = [],
    weight = [],
    underWaterWeight = [],
    groundLevel = 0,
    waterLevel,
    name = [],
    cohesion = [],
    friction = [],
    genesis = [],

    pileLength,
    pileShape,
    pileHeadSpot
}) => {
    const soilProfileRef = useRef(null);
    const [soilLayer, setSoilLayer] = useState([]);
    const [waterHeight, setWaterHeight] = useState('');
    const [isPileVisible, setIsPileVisible] = useState(false);
    const [pileHeadSpotPx, setPileHeadSpotPx] = useState('');

    const [maxHeight, setMaxHeight] = useState('');
    const [minSpotVal, setMinSpotVal] = useState(0);
    const [offsetHeight, setOffsetHeight] = useState('');
    const [profilePileLength, setProfilePileLength] = useState('');
    useEffect(() => {
        const val = Math.min(
            ...[waterLevel, pileHeadSpot].filter((ele) => isNumber(ele)),
        );
        setMinSpotVal(isNumber(val) && val < 0 ? Math.abs(val) : 0);
    }, [waterLevel, pileHeadSpot]);

    useDeepCompareEffect(() => {
        const maxValueHeight = Math.max(...height) + minSpotVal;

        setMaxHeight(isNumber(maxValueHeight) ? maxValueHeight : '');

        if (soilProfileRef.current) {
            setOffsetHeight(soilProfileRef.current.offsetHeight);
        }
    }, [height, minSpotVal]);

    useDeepCompareEffect(() => {
        const profileMaxHeight = Math.max(...height);

        setIsPileVisible(
            isNumber(pileHeadSpot) &&
                isNumber(pileLength) &&
                pileLength > 0 &&
                profileMaxHeight >= pileHeadSpot + pileLength,
        );

        if (soilProfileRef.current) {
            setProfilePileLength(
                maxHeight === 0 ? 0 : (offsetHeight * pileLength) / maxHeight,
            );
        }
    }, [pileLength, pileHeadSpot, height, maxHeight, offsetHeight]);

    useEffect(() => {
        const currentMicropileSpot =
            pileHeadSpot >= 0
                ? (offsetHeight * (pileHeadSpot + minSpotVal)) / maxHeight
                : pileHeadSpot > waterLevel
                ? (offsetHeight * (minSpotVal - Math.abs(pileHeadSpot))) /
                  maxHeight
                : 0;

        setPileHeadSpotPx(maxHeight === 0 ? 0 : currentMicropileSpot);
    }, [
        offsetHeight,
        waterLevel,
        pileHeadSpot,
        maxHeight,
        minSpotVal,
    ]);

    useDeepCompareEffect(() => {
        if (soilProfileRef.current) {
            const diff = Math.max(...height);

            setWaterHeight(
                diff === 0 || !isNumber(waterLevel)
                    ? 0
                    : (soilProfileRef.current.offsetHeight * waterLevel) / diff,
            );

            const layers = height.map((ele, i, arr) => {
                return (
                    <SoilLayer
                        key={i}
                        backgroundColor={setSoilColor({
                            id: density[i],
                            il: plasticity[i],
                        })}
                        name={name[i]}
                        height={
                            diff === 0
                                ? 0
                                : soilProfileRef.current.offsetHeight *
                                  ((i === 0 ? ele : ele - arr[i - 1]) / diff)
                        }
                        layerHeight={ele}
                        density={density[i]}
                        plasticity={plasticity[i]}
                        weight={weight[i]}
                        underWaterWeight={underWaterWeight[i]}
                        cohesion={cohesion[i]}
                        friction={friction[i]}
                        edometricModule={edometricModule[i]}
                        genesis={genesis[i]}
                        groundLevel={groundLevel}
                    />
                );
            });
            setSoilLayer(layers);
        }
    }, [
        waterLevel,
        height,
        density,
        plasticity,
        edometricModule,
        weight,
        underWaterWeight,
        name,
        cohesion,
        friction,
        genesis,
    ]);

    return (
        <Fragment>
            <SectionTitle title="Profil gruntowy" />
            {height.length > 0 ? (
                <Fragment>
                    <div className={styles.groundLevel}>
                        <span className={styles.spot}>
                            0.00{' '}
                            {`${
                                isNumber(groundLevel)
                                    ? `(${roundNumber(groundLevel, 2).toFixed(
                                          2,
                                      )} m.n.p.m.)`
                                    : '' 
                            }`}
                        </span>
                    </div>
                    <div className={styles.wrapper} ref={soilProfileRef}>
                    {isPileVisible && (
                            <div
                                className={styles.pileWrapper}
                                style={{
                                    height: `${profilePileLength}px`,
                                    top: `${pileHeadSpotPx}px`,
                                }}>
                                <div className={styles.pile}>
                                    <div
                                        className={`${styles.pileShape} ${
                                            pileShape === 'Kołowy'
                                                ? styles.circle
                                                : ''
                                        }`}></div>
                                </div>
                                <div className={styles.spotWrapper}>
                                    <span className={styles.pileSpot}>
                                        {isNumber(pileHeadSpot)
                                            ? (-pileHeadSpot).toFixed(2)
                                            : ''}{' '}
                                        {`${
                                            isNumber(groundLevel)
                                                ? `(${roundNumber(
                                                      groundLevel -
                                                          pileHeadSpot,
                                                      2,
                                                  ).toFixed(2)})`
                                                : ''
                                        }`}
                                    </span>
                                    <span className={styles.pileSpot}>
                                        {isNumber(pileHeadSpot + pileLength)
                                            ? (-(
                                                  pileHeadSpot + pileLength
                                              )).toFixed(2)
                                            : ''}{' '}
                                        {`${
                                            isNumber(groundLevel)
                                                ? `(${roundNumber(
                                                      groundLevel -
                                                          (pileHeadSpot +
                                                              pileLength),
                                                      2,
                                                  ).toFixed(2)})`
                                                : ''
                                        }`}
                                    </span>
                                </div>
                            </div>
                        )}
                        {isNumber(waterLevel) &&
                            soilProfileRef.current &&
                            soilProfileRef.current.offsetHeight >
                                waterHeight && (
                                <div
                                    className={`${styles.zwg} ${
                                        typeof waterLevel === 'number'
                                            ? ''
                                            : styles.hiddenZwg
                                    }`}
                                    style={{ top: waterHeight || 0 }}>
                                    <span className={styles.zwgLevel}>
                                        {(-waterLevel).toFixed(2)}{' '}
                                        {`${
                                            isNumber(groundLevel)
                                                ? `(${roundNumber(
                                                      groundLevel - waterLevel,
                                                      2,
                                                  ).toFixed(2)})`
                                                : ''
                                        }`}
                                    </span>
                                    <div className={styles.zwgSpot}></div>
                                </div>
                            )}
                        <div>
                            {[...soilLayer]}
                        </div>
                    </div>
                </Fragment>
            ) : (
                <div className={styles.soilsProps}>
                    <FontAwesomeIcon icon={faBan} />
                    <p className={styles.p}>Uzupełnij parametry gruntu</p>
                </div>
            )}
        </Fragment>
    );
};

export default React.memo(
    SoilProfile,
    (prev, next) => stringify(prev) === stringify(next),
);
